import { useAuth0 } from '@auth0/auth0-react'
import { useRouter } from '@components/Router/useRouter'
import { Feature } from '@lib/graphql/__generated__/graphql'
import { getOrganizationId } from '@lib/graphql/variables/organizationId'
import useUser from '@lib/hooks/useUser'
import { handleOrganizationRoutes } from '@mono/packages/utils/router'
import {
  ADMIN_MODULE_FEATURES,
  ADMIN_MODULE_PERMISSIONS,
  MAIN_NAVIGATION_FEATURES,
  MAIN_NAVIGATION_PERMISSIONS,
  PERMISSIONS,
} from '@utils/constants/permissions'
import { ROUTER_PATHS } from '@utils/constants/routerPaths'
import { useEffect, useMemo, useState } from 'react'

const rootPaths = [
  ROUTER_PATHS.ORGANIZATION_PICKER,
  ROUTER_PATHS.NOT_FOUND,
  ROUTER_PATHS.FORBIDDEN,
  ROUTER_PATHS.ERROR,
  ROUTER_PATHS.SERVER_ERROR,
  ROUTER_PATHS.INVITATIONS,
  ROUTER_PATHS.GOOGLE_CALENDAR_AUTHORIZE,
  ROUTER_PATHS.MICROSOFT_CALENDAR_AUTHORIZE,
  ROUTER_PATHS.GOOGLE_EMAIL_AUTHORIZE,
  ROUTER_PATHS.MICROSOFT_EMAIL_AUTHORIZE,
]

const userHasPermission = ({
  userPermissions,
  enabledFeatures,
  permission,
  feature,
}: {
  userPermissions: string[]
  enabledFeatures: Feature[]
  permission: string | string[]
  feature?: Feature
}) => {
  const isFeature = !!feature
  const permissionsArray = Array.isArray(permission) ? permission : [permission]
  const hasPermissions = permissionsArray.some((p) =>
    userPermissions.includes(p),
  )

  if (isFeature) {
    return enabledFeatures?.includes(feature) && hasPermissions
  }

  return hasPermissions
}

const hasPagePermissions = ({
  pathname,
  permissions,
  enabledFeatures,
}: {
  pathname
  permissions: string[]
  enabledFeatures: Feature[]
}) => {
  const mainPath = pathname.split('/')[2]

  if (mainPath === 'management') {
    return userHasPermission({
      userPermissions: permissions,
      enabledFeatures,
      permission: PERMISSIONS.ORGANIZATION_MANAGE,
      feature: null,
    })
  }

  if (mainPath === 'administration' || mainPath === 'user-management') {
    const splitted = pathname.split('/')
    const lastSegment = splitted[splitted.length - 1]
    const pageModule = lastSegment.split('?')[0]

    if (!pageModule) return false

    return userHasPermission({
      userPermissions: permissions,
      enabledFeatures,
      permission: ADMIN_MODULE_PERMISSIONS[pageModule],
      feature: ADMIN_MODULE_FEATURES[pageModule],
    })
  }

  if (mainPath) {
    const mainPathRoute = `/${mainPath}`
    const mainNavigationPermission = MAIN_NAVIGATION_PERMISSIONS[mainPathRoute]

    if (!mainNavigationPermission) {
      // If we don't have registered permissions for this route we allow the route
      return true
    }

    if (mainPathRoute.includes(ROUTER_PATHS.REPORTS)) {
      // For reports route we check if we have at least one report permission
      return !!permissions.find((p) =>
        p.includes(PERMISSIONS.FE_NAVIGATION_REPORTS_PREFIX),
      )
    }

    return userHasPermission({
      userPermissions: permissions,
      enabledFeatures,
      permission: mainNavigationPermission,
      feature: MAIN_NAVIGATION_FEATURES[mainPathRoute],
    })
  }

  return false
}

const RedirectHandler = ({ children }) => {
  const router = useRouter()
  const { isAuthenticated, isLoading } = useAuth0()
  const { account } = useUser({ skip: !isAuthenticated })

  const [ready, setReady] = useState(false)

  const permissions = useMemo(() => account?.permissionsNames ?? [], [account])
  const enabledFeatures = account?.organization?.enabledFeatures

  useEffect(() => {
    if (!isAuthenticated || !account || isLoading) return

    const replaceWithReady = (path: string) => {
      router.replaceRoot(path).then(() => setReady(true))
    }

    const pathname = router.asPath

    const orgId = getOrganizationId()

    if (pathname === '/') {
      replaceWithReady(`/${orgId}${ROUTER_PATHS.DASHBOARD}`)

      return
    }

    if (rootPaths.find((path) => pathname.includes(path))) {
      //eslint-disable-next-line
      setReady(true)
      return
    }

    const url = handleOrganizationRoutes({
      pathname,
      orgId,
      switchOrganizationPath: ROUTER_PATHS.SWITCH_ORGANIZATION,
    })

    if (url) {
      replaceWithReady(url)
      return
    }

    const userAuthorized = hasPagePermissions({
      pathname,
      enabledFeatures,
      permissions,
    })

    if (!userAuthorized) {
      replaceWithReady(`/${orgId}${ROUTER_PATHS.DASHBOARD}`)
      return
    }

    setReady(true)
  }, [
    router,
    permissions,
    enabledFeatures,
    isAuthenticated,
    isLoading,
    account,
  ])

  if (!ready || !isAuthenticated || isLoading) return null

  return children
}

export default RedirectHandler
